import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { webColors, webBorderRadius } from '../../../constants/webStyles';
import { useIsMobile } from '../../../hooks/useIsMobile';

// Components
import AuthHeader from './components/AuthHeader.web';
import BrandingPanel from './components/BrandingPanel.web';
import { WebProgressSteps } from '../../../components/web';

// Steps
import StepCountry from './steps/StepCountry.web';
import StepProfession from './steps/StepProfession.web';
import StepDetails from './steps/StepDetails.web';
import StepVerify from './steps/StepVerify.web';
import StepSuccess from './steps/StepSuccess.web';

// Views
import LoginView from './views/LoginView.web';
import ForgotPasswordView from './views/ForgotPasswordView.web';

const STEPS = [
  { label: 'Country', component: StepCountry },
  { label: 'Profession', component: StepProfession },
  { label: 'Details', component: StepDetails },
  { label: 'Verify', component: StepVerify },
];

const DoctorAuth = () => {
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const [currentView, setCurrentView] = useState('register'); // 'register' | 'login' | 'forgotPassword'
  const [currentStep, setCurrentStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [formData, setFormData] = useState({
    country: null,
    profession: null,
    professionName: '',
    specialization: null,
    specializationName: '',
    professions: [],
    profession_ids: [],
    profession_id: null,
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    state: '',
    termsAccepted: false,
    otp: '',
    nonce: null,
  });

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= STEPS.length) {
      setCurrentStep(step);
    }
  };

  const handleComplete = () => {
    setIsComplete(true);
  };

  // View handlers
  const showLogin = () => setCurrentView('login');
  const showRegister = () => {
    setCurrentView('register');
    // Reset to step 1 when coming back to register
    if (isComplete) {
      setIsComplete(false);
      setCurrentStep(1);
      setFormData({
        country: null,
        profession: null,
        professionName: '',
        specialization: null,
        specializationName: '',
        professions: [],
        profession_ids: [],
        profession_id: null,
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        state: '',
        termsAccepted: false,
        otp: '',
        nonce: null,
      });
    }
  };
  const showForgotPassword = () => setCurrentView('forgotPassword');

  // Render success screen if registration complete
  if (currentView === 'register' && isComplete) {
    return <StepSuccess onShowLogin={showLogin} />;
  }

  const CurrentStepComponent = STEPS[currentStep - 1]?.component;
  const isDoctor = true; // This is the doctor-only flow

  return (
    <View style={styles.container}>
      {/* Header */}
      <AuthHeader
        currentView={currentView}
        onShowLogin={showLogin}
        onShowRegister={showRegister}
      />

      {/* Progress Steps - Only for register view */}
      {currentView === 'register' && (
        <WebProgressSteps steps={STEPS} currentStep={currentStep} />
      )}

      {/* Main Content */}
      <View style={[styles.mainContainer, isMobile && styles.mainContainerMobile]}>
        {/* Left Side - Branding */}
        <BrandingPanel />

        {/* Right Side - Form */}
        <ScrollView style={styles.formSide} contentContainerStyle={[styles.formSideContent, isMobile && styles.formSideContentMobile]}>
          <View style={styles.formContainer}>
            {currentView === 'register' && CurrentStepComponent && (
              <CurrentStepComponent
                formData={formData}
                updateFormData={updateFormData}
                goToStep={goToStep}
                currentStep={currentStep}
                onComplete={handleComplete}
                isDoctor={isDoctor}
              />
            )}

            {currentView === 'login' && (
              <LoginView
                onShowForgotPassword={showForgotPassword}
                onShowRegister={showRegister}
                navigation={navigation}
              />
            )}

            {currentView === 'forgotPassword' && (
              <ForgotPasswordView
                onShowLogin={showLogin}
              />
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: webColors.background,
    minHeight: '100vh',
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 'calc(100vh - 140px)',
  },
  mainContainerMobile: {
    flexDirection: 'column',
    minHeight: 'auto',
  },
  formSide: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
  },
  formSideContent: {
    padding: 40,
    justifyContent: 'center',
    minHeight: '100%',
  },
  formSideContentMobile: {
    padding: 20,
  },
  formContainer: {
    maxWidth: 480,
    marginHorizontal: 'auto',
    width: '100%',
  },
});

export default DoctorAuth;
